////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
*/
// ##                                                                                                               ABOUT PAGE PEA
/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
*/

/**
 * BitsBound About Page - Pea Component
 *
 * Pure presentational component for the About BitsBound page.
 * Showcases the Platform + Advisory positioning, founder story, and company vision.
 *
 * TIR{ADVT}O Pattern:
 * - Trigger: Component mount / navigation to /about
 * - Input: N/A (static content page)
 * - Requirements{ADVT}:
 *   - A: N/A
 *   - D: CSS styles, content sections, team data
 *   - V: N/A
 *   - T: Render about page sections
 * - Output: React elements for About page
 */

import React, { useState } from 'react';
import styles from './Bitsbound_Kings_Home_Primary_SRC_About_Lobby_Pea.module.css';

// D: Founder headshot image - copied to dist/images/ during build by vite.config.ts copy-rob-taylor-headshot plugin
const founderHeadshot = '/images/rob-taylor-headshot.jpeg';

// ============================================
// DEFINITIONS 'D' - About Page Content
// ============================================

/**
 * D_FOUNDER: Rob Taylor's background and credentials
 */
const FOUNDER_INFO = {
  name: 'Robert J. Taylor, Esq.',
  title: 'Founder & CEO',
  credentials: [
    'J.D., Penn State Dickinson Law (Dean\'s Scholarship)',
    'Licensed: Pennsylvania, New Jersey, New York',
    'CIPP/E Certified (Privacy Professional)',
    'CISSP Candidate (Security)',
  ],
  experience: [
    {
      company: 'Boomi, LP (Dell Technologies)',
      role: 'Legal Counsel',
      period: '2021-2024',
      highlights: [
        'Led multimillion-dollar ACV negotiations with Fortune 500 companies',
        'Spearheaded acquisitions for IP including software and patents',
        'Built procurement function post-divestiture from Dell',
      ],
    },
    {
      company: 'USLI (Berkshire Hathaway)',
      role: 'Corporate Counsel',
      period: '2019-2021',
      highlights: [
        'Negotiated supplier agreements (SaaS, software, legal services)',
        'Developed data privacy/security frameworks (GLBA, CCPA, NYDFS)',
      ],
    },
    {
      company: 'Wilson Elser LLP',
      role: 'Associate Attorney',
      period: '2017-2019',
      highlights: [
        'Managed complex civil litigation in multiple jurisdictions',
      ],
    },
  ],
  tagline: 'Corporate technology attorney turned full-stack AI engineer. The rare combination of legal judgment + engineering execution.',
};


/**
 * D_DIFFERENTIATORS: What sets BitsBound apart
 */
const DIFFERENTIATORS = [
  {
    title: 'Attorney + Engineer',
    description: 'Built by someone who has negotiated the contracts AND written the code. Not a consultant who talks about AI—an engineer who builds it.',
  },
  {
    title: 'Partner-Level Quality',
    description: 'Every output is designed to meet the standard a senior partner would expect. First-draft acceleration, not first-draft replacement.',
  },
  {
    title: 'Real Track Changes',
    description: 'Actual OOXML Track Changes (w:ins, w:del) on your original document—not highlighted copies or PDFs.',
  },
];

/**
 * D_VISION: Company vision and mission
 */
const VISION = {
  mission: 'Turn contracts and legal workflows into executable systems.',
  belief: 'Law and code are the same logical structures written in different syntax. BitsBound is the syntax translator.',
  future: 'A world where every B2B agreement is a living, self-executing system—not a static PDF collecting dust.',
};

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * HeroSection: Main hero banner for About page
 */
function HeroSection(): React.ReactElement {
  return (
    <section className={styles.heroSection}>
      <div className={styles.heroContent}>
        <div className={styles.heroBadge}>ABOUT BITSBOUND</div>
        <h1 className={styles.heroTitle}>
          Agentic AI <span className={styles.highlightTeal}>Contract Automation</span>
        </h1>
        <p className={styles.heroSubtitle}>
          An AI-native contract automation platform for legal teams. Redline counterparty paper OR draft your own.
        </p>
        <p className={styles.heroDescription}>
          {VISION.mission}
        </p>
      </div>
    </section>
  );
}

/**
 * FounderSection: Rob Taylor's story and credentials
 */
function FounderSection(): React.ReactElement {
  return (
    <section className={styles.founderSection}>
      <div className={styles.founderHero}>
        {/* Founder Headshot */}
        <div className={styles.founderImageContainer}>
          <img
            src={founderHeadshot}
            alt="Robert J. Taylor, Esq. - Founder & CEO of BitsBound"
            className={styles.founderImage}
          />
          <div className={styles.founderImageGlow}></div>
        </div>

        {/* Founder Info */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionLabel}>THE FOUNDER</div>
          <h2 className={styles.sectionTitle}>{FOUNDER_INFO.name}</h2>
          <p className={styles.founderTagline}>{FOUNDER_INFO.tagline}</p>
        </div>
      </div>

      <div className={styles.founderGrid}>
        {/* Credentials Card */}
        <div className={styles.founderCard}>
          <h3 className={styles.cardTitle}>Credentials</h3>
          <ul className={styles.credentialsList}>
            {FOUNDER_INFO.credentials.map((credential, index) => (
              <li key={index} className={styles.credentialItem}>
                <span className={styles.checkmark}>✓</span>
                {credential}
              </li>
            ))}
          </ul>
        </div>

        {/* Experience Timeline */}
        <div className={styles.founderCard}>
          <h3 className={styles.cardTitle}>Experience</h3>
          <div className={styles.timeline}>
            {FOUNDER_INFO.experience.map((exp, index) => (
              <div key={index} className={styles.timelineItem}>
                <div className={styles.timelineDot}></div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineHeader}>
                    <span className={styles.timelineCompany}>{exp.company}</span>
                    <span className={styles.timelinePeriod}>{exp.period}</span>
                  </div>
                  <div className={styles.timelineRole}>{exp.role}</div>
                  <ul className={styles.timelineHighlights}>
                    {exp.highlights.map((highlight, hIndex) => (
                      <li key={hIndex}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


/**
 * DifferentiatorsSection: Why BitsBound is unique
 */
function DifferentiatorsSection(): React.ReactElement {
  return (
    <section className={styles.differentiatorsSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionLabel}>THE DIFFERENCE</div>
        <h2 className={styles.sectionTitle}>Why BitsBound</h2>
      </div>

      <div className={styles.differentiatorsGrid}>
        {DIFFERENTIATORS.map((diff, index) => (
          <div key={index} className={styles.differentiatorCard}>
            <h3 className={styles.differentiatorTitle}>{diff.title}</h3>
            <p className={styles.differentiatorDescription}>{diff.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * VisionSection: Company vision and philosophy
 */
function VisionSection(): React.ReactElement {
  return (
    <section className={styles.visionSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionLabel}>THE VISION</div>
        <h2 className={styles.sectionTitle}>Where We're Going</h2>
      </div>

      <div className={styles.visionContent}>
        <div className={styles.visionCard}>
          <div className={styles.visionIcon}>💡</div>
          <h3 className={styles.visionLabel}>Our Belief</h3>
          <p className={styles.visionText}>{VISION.belief}</p>
        </div>

        <div className={styles.visionCard}>
          <div className={styles.visionIcon}>🚀</div>
          <h3 className={styles.visionLabel}>Our Future</h3>
          <p className={styles.visionText}>{VISION.future}</p>
        </div>
      </div>
    </section>
  );
}

/**
 * EmailModal: Overlay modal showing contact email
 */
interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function EmailModal({ isOpen, onClose }: EmailModalProps): React.ReactElement | null {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('admin@bitsbound.com');
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button className={styles.modalClose} onClick={onClose}>×</button>
        <h3 className={styles.modalTitle}>Contact Us</h3>
        <p className={styles.modalDescription}>
          Send us an email and we'll get back to you within 24 hours.
        </p>
        <div className={styles.emailDisplay}>
          <span className={styles.emailAddress}>admin@bitsbound.com</span>
          <button className={styles.copyButton} onClick={handleCopyEmail} title="Copy email">
            📋
          </button>
        </div>
        <a href="mailto:admin@bitsbound.com" className={styles.emailLink}>
          Open Email Client
        </a>
      </div>
    </div>
  );
}

/**
 * CTASection: Call to action
 */
interface CTASectionProps {
  onEmailClick: () => void;
}

function CTASection({ onEmailClick }: CTASectionProps): React.ReactElement {
  return (
    <section className={styles.ctaSection}>
      <div className={styles.ctaContent}>
        <h2 className={styles.ctaTitle}>Get in Touch</h2>
        <p className={styles.ctaDescription}>
          Have questions about BitsBound? We'd love to hear from you.
        </p>
        <div className={styles.ctaButtons}>
          <button className={styles.ctaPrimary} onClick={onEmailClick}>
            Email Us
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// MAIN ABOUT PAGE COMPONENT
// ============================================

/**
 * AboutPea - Main About Page Component
 *
 * TIR{ADVT}O Pattern:
 * - Trigger: Navigation to /about route
 * - Input: None (static content)
 * - Requirements{ADVT}:
 *   - D: Content constants defined above
 *   - T: Render all sections in order
 * - Output: Complete About page
 */
export function AboutPea(): React.ReactElement {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  return (
    <div className={styles.aboutContainer}>
      {/* Navigation Header */}
      <header className={styles.aboutHeader}>
        <a href="/" className={styles.logoLink}>
          <span className={styles.logoText}>BitsBound</span>
        </a>
        <nav className={styles.aboutNav}>
          <a href="/" className={styles.navLink}>Home</a>
          <a href="/about" className={styles.navLinkActive}>About</a>
          <button
            onClick={() => setIsEmailModalOpen(true)}
            className={styles.navLink}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Contact
          </button>
        </nav>
      </header>

      {/* Main Content */}
      <main className={styles.aboutMain}>
        <HeroSection />
        <FounderSection />
        <DifferentiatorsSection />
        <VisionSection />
        <CTASection onEmailClick={() => setIsEmailModalOpen(true)} />
      </main>

      {/* Email Modal */}
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />

      {/* Footer */}
      <footer className={styles.aboutFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <span className={styles.footerLogo}>BitsBound</span>
            <p className={styles.footerTagline}>
              AI-native contract automation platform.
            </p>
          </div>
          <div className={styles.footerLinks}>
            <a href="/privacy" className={styles.footerLink}>Privacy</a>
            <a href="/terms" className={styles.footerLink}>Terms</a>
            <button
              onClick={() => setIsEmailModalOpen(true)}
              className={styles.footerLink}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Contact
            </button>
          </div>
          <div className={styles.footerCopyright}>
            <p>© 2025 BitsBound, Inc. All rights reserved.</p>
            <p className={styles.disclaimer}>
              BitsBound provides AI-assisted contract analysis tools for legal professionals.
              All outputs are preliminary drafts requiring attorney review and do not constitute legal advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Legacy export for backwards compatibility
export const LobbyPea = AboutPea;

export default AboutPea;
